class SlideOutPanel extends HTMLElement {
  get panelSelector() {
    return this.dataset.panel || '[role="dialog"]';
  }

  get openTriggerSelector() {
    return this.dataset.openTrigger || '[data-slide-out-open]';
  }

  connectedCallback() {
    this.panel = this.querySelector(this.panelSelector);
    this.overlay = this.querySelector('[data-slide-out-overlay]');
    this.body = document.body;
    this.activeElement = null;
    this.onDocumentClick = this.handleDocumentClick.bind(this);
    this.onKeyUp = this.handleKeyUp.bind(this);

    if (!this.panel) return;

    this.querySelector('[data-slide-out-close]')?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('click', this.onDocumentClick);
    this.addEventListener('keyup', this.onKeyUp);

    this.onConnected();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    this.removeEventListener('keyup', this.onKeyUp);
    this.onDisconnected();
  }

  onConnected() {}

  onDisconnected() {}

  handleDocumentClick(event) {
    const trigger = event.target.closest(this.openTriggerSelector);
    if (!trigger) return;

    event.preventDefault();
    this.open({
      trigger,
      ...this.getOpenOptionsFromTrigger(trigger),
    });
  }

  getOpenOptionsFromTrigger(trigger) {
    return {};
  }

  handleKeyUp(event) {
    if (event.code === 'Escape') this.close();
  }

  onBeforeOpen(_options) {}

  onAfterOpen(_options) {}

  onBeforeClose() {}

  onAfterClose() {}

  open(options = {}) {
    const { trigger } = options;
    if (trigger) this.activeElement = trigger;

    document.querySelectorAll('.slide-out-shell.is-open').forEach((shell) => {
      if (shell !== this && typeof shell.close === 'function') shell.close();
    });

    this.onBeforeOpen(options);

    this.classList.remove('is-closing');
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');

    if (this.overlay) {
      this.overlay.classList.add('active');
      requestAnimationFrame(() => {
        this.overlay.classList.add('is-visible');
      });
    }

    this.panel.addEventListener('transitionend', () => {
      this.panel.focus();
      if (typeof trapFocus === 'function') {
        trapFocus(this.panel);
      }
    }, { once: true });

    if (typeof enableScrollLock === 'function') {
      requestAnimationFrame(() => enableScrollLock(this));
    } else {
      this.body.classList.add('tw-overflow-hidden');
    }

    this.onAfterOpen(options);
  }

  close() {
    this.onBeforeClose();

    this.classList.add('is-closing');
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');

    if (this.overlay) {
      this.overlay.classList.remove('is-visible');
      const onFadeEnd = (event) => {
        if (event.propertyName && event.propertyName !== 'opacity') return;
        this.overlay.classList.remove('active');
        this.overlay.removeEventListener('transitionend', onFadeEnd);
      };
      this.overlay.addEventListener('transitionend', onFadeEnd);
    }

    const onCloseEnd = (event) => {
      if (event.target !== this.panel) return;
      if (event.propertyName && event.propertyName !== 'transform') return;
      this.classList.remove('is-closing');
      this.panel.removeEventListener('transitionend', onCloseEnd);
    };
    this.panel.addEventListener('transitionend', onCloseEnd);

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    if (typeof disableScrollLock === 'function') {
      disableScrollLock(this);
    } else {
      this.body.classList.remove('tw-overflow-hidden');
    }

    this.onAfterClose();
  }
}

class TabbedSlideOutPanel extends SlideOutPanel {
  onConnected() {
    this.tabs = Array.from(this.querySelectorAll('[data-slide-out-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-slide-out-panel]'));

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.selectTab(tab.dataset.slideOutTab));
      tab.addEventListener('keydown', (event) => this.handleTabKeydown(event));
    });
  }

  getOpenOptionsFromTrigger(trigger) {
    const tabId = trigger.getAttribute('data-slide-out-open');
    return {
      tabId: tabId || undefined,
    };
  }

  handleTabKeydown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    if (currentIndex < 0) return;

    let nextIndex = currentIndex;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;

    const nextTab = this.tabs[nextIndex];
    if (!nextTab) return;

    this.selectTab(nextTab.dataset.slideOutTab);
    nextTab.focus();
  }

  onBeforeOpen({ tabId } = {}) {
    if (tabId) {
      this.selectTab(tabId);
      return;
    }

    if (!this.querySelector('[data-slide-out-tab].is-active') && this.tabs[0]) {
      this.selectTab(this.tabs[0].dataset.slideOutTab);
    }
  }

  selectTab(tabId) {
    if (!tabId) return false;

    let matched = false;

    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.slideOutTab === tabId;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
      if (isActive) matched = true;
    });

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.slideOutPanel === tabId;
      panel.classList.toggle('is-active', isActive);
      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    return matched;
  }
}

window.SlideOutPanel = SlideOutPanel;
window.TabbedSlideOutPanel = TabbedSlideOutPanel;

if (!customElements.get('slide-out-panel')) {
  customElements.define('slide-out-panel', TabbedSlideOutPanel);
}
